import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save } from 'lucide-react';
import { Habit } from '../types';
import styles from './CompleteHabitModal.module.css';

interface CompleteHabitModalProps {
  habit: Habit;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (customValue?: string | number, notes?: string) => void;
}

const CompleteHabitModal: React.FC<CompleteHabitModalProps> = ({ 
  habit, 
  isOpen, 
  onClose, 
  onComplete 
}) => {
  const [customValue, setCustomValue] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const finalValue = habit.hasCustomValue && customValue ? 
        (habit.customValueType === 'number' ? parseFloat(customValue) : customValue) : 
        undefined;
      
      await onComplete(finalValue, notes.trim() || undefined);
      
      // Reset form
      setCustomValue('');
      setNotes('');
      onClose();
    } catch (error) {
      console.error('Erro ao completar hábito:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickComplete = async () => {
    setIsSubmitting(true);
    try {
      await onComplete();
      onClose();
    } catch (error) {
      console.error('Erro ao completar hábito:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.backdrop}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={styles.modalContent}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.statusIndicator}></div>
              <h2 className={styles.title}>
                Completar Hábito
              </h2>
            </div>
            <button
              onClick={onClose}
              className={styles.closeButton}
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className={styles.content}>
            <div className={styles.habitInfo}>
              <h3 className={styles.habitName}>{habit.name}</h3>
              <p className={styles.habitMeta}>{habit.duration} min • {habit.difficulty}</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>              {/* Campo de Valor Personalizado */}
              {habit.hasCustomValue && (
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {habit.customValueLabel || 'Valor'} 
                    {habit.customValueUnit && ` (${habit.customValueUnit})`}
                  </label>
                  <input
                    type={habit.customValueType === 'number' ? 'number' : 'text'}
                    value={customValue}
                    onChange={(e) => setCustomValue(e.target.value)}
                    placeholder={`Ex: ${habit.customValueType === 'number' ? '30' : 'Valor'}`}
                    className={styles.input}
                    step={habit.customValueType === 'number' ? '0.1' : undefined}
                  />
                </div>
              )}

              {/* Campo de Notas */}
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  Notas (opcional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Como foi? Alguma observação..."
                  rows={3}
                  className={styles.textarea}
                />
              </div>              {/* Botões */}
              <div className={styles.buttonGroup}>
                {habit.hasCustomValue ? (
                  <>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={styles.primaryButton}
                    >
                      {isSubmitting ? (
                        <div className={styles.spinner}></div>
                      ) : (
                        <>
                          <Save size={16} />
                          <span>Completar</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleQuickComplete}
                      disabled={isSubmitting}
                      className={styles.secondaryButton}
                    >
                      Rápido
                    </button>
                  </>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={styles.fullWidthButton}
                  >
                    {isSubmitting ? (
                      <div className={styles.spinner}></div>
                    ) : (
                      <>
                        <Save size={16} />
                        <span>Completar Hábito</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CompleteHabitModal;
